/** Maps combo-course hole positions to the 54 physical holes across ANCC campuses. */

import { COMBO_NINES, PHYSICAL_NINE_PARS, type NineName } from './physical-nine-pars';

export { COMBO_NINES, PHYSICAL_NINE_PARS } from './physical-nine-pars';

export interface FantasyHoleDef {
  id: string;
  campus: 'Arlington' | 'Fairfax';
  nine: 'Blue' | 'Red' | 'White';
  holeInNine: number;
  label: string;
  par: number;
}

const NINE_ORDER: NineName[] = ['blue', 'red', 'white'];

function capitalizeNine(n: NineName): 'Blue' | 'Red' | 'White' {
  return (n.charAt(0).toUpperCase() + n.slice(1)) as 'Blue' | 'Red' | 'White';
}

export function buildFantasyHoles(): FantasyHoleDef[] {
  const holes: FantasyHoleDef[] = [];
  for (const campus of ['Arlington', 'Fairfax'] as const) {
    const prefix = campus === 'Arlington' ? 'arl' : 'ffx';
    for (const nine of NINE_ORDER) {
      const pars = PHYSICAL_NINE_PARS[`${prefix}-${nine}`] || Array(9).fill(4);
      for (let i = 0; i < 9; i += 1) {
        const holeInNine = i + 1;
        holes.push({
          id: `${prefix}-${nine}-${holeInNine}`,
          campus,
          nine: capitalizeNine(nine),
          holeInNine,
          label: `${campus.slice(0, 3).toUpperCase()} ${capitalizeNine(nine)} ${holeInNine}`,
          par: pars[i] ?? 4,
        });
      }
    }
  }
  return holes;
}

export const FANTASY_HOLES = buildFantasyHoles();

export type FantasyCampusFilter = 'All' | 'Arlington' | 'Fairfax';

export function fantasyHolesForCampus(campus: FantasyCampusFilter): FantasyHoleDef[] {
  if (campus === 'All') return FANTASY_HOLES;
  return FANTASY_HOLES.filter((h) => h.campus === campus);
}

const FANTASY_PAR_BY_ID = Object.fromEntries(FANTASY_HOLES.map((h) => [h.id, h.par])) as Record<string, number>;

export function parForFantasyHole(fantasyKey: string): number {
  return FANTASY_PAR_BY_ID[fantasyKey] ?? 4;
}

export function physicalNineKey(fantasyKey: string): string | null {
  const parts = fantasyKey.split('-');
  if (parts.length < 3) return null;
  return `${parts[0]}-${parts[1]}`;
}

export function fantasyHolesForPhysicalNine(physicalNineKey: string): FantasyHoleDef[] {
  return FANTASY_HOLES.filter((h) => h.id.startsWith(`${physicalNineKey}-`));
}

export function fantasyHoleKey(courseSlug: string, holeNum: number): string | null {
  const nines = COMBO_NINES[courseSlug];
  if (!nines || holeNum < 1 || holeNum > 18) return null;
  const campusPrefix = courseSlug.startsWith('arl') ? 'arl' : courseSlug.startsWith('ffx') ? 'ffx' : null;
  if (!campusPrefix) return null;
  const nineIndex = holeNum <= 9 ? 0 : 1;
  const holeInNine = holeNum <= 9 ? holeNum : holeNum - 9;
  return `${campusPrefix}-${nines[nineIndex]}-${holeInNine}`;
}

export interface HoleRoundEntry {
  playedAt: string;
  strokes: number;
  courseName?: string;
  putts?: number | null;
}

export interface HoleAnalytics {
  hole: FantasyHoleDef;
  best: number | null;
  worst: number | null;
  average: number | null;
  roundsPlayed: number;
  birdies: number;
  pars: number;
  bogeys: number;
  history: HoleRoundEntry[];
}

export function buildHoleAnalytics(
  holeId: string,
  entries: Array<{ fantasyKey: string; playedAt: string; strokes: number; courseName?: string; putts?: number | null }>,
): HoleAnalytics | null {
  const hole = FANTASY_HOLES.find((h) => h.id === holeId);
  if (!hole) return null;
  const mine = entries.filter((e) => e.fantasyKey === holeId && e.strokes > 0);
  if (!mine.length) {
    return {
      hole,
      best: null,
      worst: null,
      average: null,
      roundsPlayed: 0,
      birdies: 0,
      pars: 0,
      bogeys: 0,
      history: [],
    };
  }
  const strokes = mine.map((e) => e.strokes);
  const best = Math.min(...strokes);
  const worst = Math.max(...strokes);
  const average = Math.round((strokes.reduce((a, b) => a + b, 0) / strokes.length) * 10) / 10;
  let birdies = 0;
  let pars = 0;
  let bogeys = 0;
  mine.forEach((e) => {
    const d = e.strokes - hole.par;
    if (d <= -1) birdies += 1;
    else if (d === 0) pars += 1;
    else bogeys += 1;
  });
  return {
    hole,
    best,
    worst,
    average,
    roundsPlayed: mine.length,
    birdies,
    pars,
    bogeys,
    history: mine
      .map((e) => ({ playedAt: e.playedAt, strokes: e.strokes, courseName: e.courseName, putts: e.putts }))
      .sort((a, b) => new Date(b.playedAt).getTime() - new Date(a.playedAt).getTime()),
  };
}

export function bestScoresByFantasyHole(
  entries: Array<{ fantasyKey: string; strokes: number }>,
): Record<string, number> {
  const best: Record<string, number> = {};
  entries.forEach((e) => {
    if (e.strokes <= 0) return;
    if (best[e.fantasyKey] == null || e.strokes < best[e.fantasyKey]) {
      best[e.fantasyKey] = e.strokes;
    }
  });
  return best;
}
